import { Col, Flex, Row, Typography } from "antd";
import dayjs from "dayjs";

const NO_ANSWER = "No Answer";

export default function QuestionContentPreview(props) {
	const {
		id = [],
		question = null,
		description = null,
		type = null,
		form_question_options = [],
		answers = {},
	} = props;

	// console.log("QuestionContentPreview props:", props);

	const renderQuestionOption = () => {
		let newId = Array.isArray(id) ? id.join("_") : id;
		const idPath = Array.isArray(id) ? id : [id];

		switch (type) {
			case "Text (Input)":
			case "Number (Input)":
			case "Date (Datepicker)":
			case "Time (Timepicker)":
			case "Radio (Button)":
			case "Checkbox":
			case "Select (Dropdown)":
				let answer = [];
				const ownAnswer = answers[newId];
				const otherAnswer = answers[`${newId}_other`];

				if (hasAnswerValue(ownAnswer)) {
					const formatted = checkTypeAnswer(type, ownAnswer);
					answer.push(
						hasAnswerValue(otherAnswer)
							? `${formatted}: ${otherAnswer}`
							: formatted,
					);
				} else if (hasAnswerValue(otherAnswer)) {
					answer.push(otherAnswer);
				}

				const indexedSubOptionAnswers = extractIndexedSubOptionAnswers(
					type,
					newId,
					form_question_options,
				);

				if (indexedSubOptionAnswers.length > 0) {
					answer.push(...indexedSubOptionAnswers);
				}

				const normalizedAnswer = answer
					.flat(Infinity)
					.filter((item) => item !== null && item !== undefined && item !== "");

				// Only the selected option's sub options are rendered in the form, so only those have answers
				const selectedValues = Array.isArray(ownAnswer)
					? ownAnswer
					: [ownAnswer];
				const isTextInput =
					type === "Text (Input)" || type === "Number (Input)";
				const selectedSubOptions = form_question_options.filter(
					(option) =>
						option.sub_option_type &&
						(isTextInput
							? !option.is_other && hasAnswerValue(ownAnswer)
							: selectedValues.includes(option.option)),
				);

				return (
					<Flex vertical style={{ paddingLeft: 20 }}>
						{normalizedAnswer.length ? (
							normalizedAnswer.map((item, index) => (
								<Flex key={index}>- {item}</Flex>
							))
						) : (
							<Flex>- {NO_ANSWER}</Flex>
						)}
						{selectedSubOptions.map((option) => (
							<QuestionContentPreview
								key={`${newId}_qo_${option.id}`}
								id={[...idPath, `qo_${option.id}`]}
								question={
									option.question_option ||
									(isTextInput || option.sub_option_type === "sub_question"
										? option.option
										: null)
								}
								type={option.sub_option_type}
								form_question_options={option.form_question_options || []}
								answers={answers}
							/>
						))}
					</Flex>
				);
			case "sub_question":
				const parentId =
					form_question_options[0]?.form_question_option_id ?? null;
				const optionsToPreview = form_question_options.filter(
					(opt) => (opt.form_question_option_id ?? null) === parentId,
				);

				return (
					<Row gutter={[20, 20]}>
						<Col xs={24} sm={24} md={24} lg={24} xl={24}></Col>
						<Col xs={24} sm={24} md={24} lg={24} xl={24}>
							{optionsToPreview.map((option) => {
								const optionPrefix = `${newId}_qo_${option.id}`;
								const optionAnswer = answers[optionPrefix];

								return (
									<Row key={optionPrefix}>
										<Col xs={24} sm={24} lg={24} xl={24} xxl={24}>
											<Flex align="center" gap={5}>
												<Typography.Text strong>
													{option.option}
												</Typography.Text>
												{!option.sub_option_type || option.option_required ? (
													<>
														<Typography.Text strong>: </Typography.Text>
														<Typography.Text>
															{hasAnswerValue(optionAnswer)
																? optionAnswer
																: NO_ANSWER}
														</Typography.Text>
													</>
												) : null}
											</Flex>
										</Col>
										<Col xs={24} sm={24} lg={24} xl={24} xxl={24}>
											<QuestionContentPreview
												id={[...idPath, `qo_${option.id}`]}
												question={option.question_option}
												type={option.sub_option_type}
												form_question_options={
													option.form_question_options.length
														? option.form_question_options
														: []
												}
												answers={answers}
											/>
										</Col>
									</Row>
								);
							})}
						</Col>
					</Row>
				);
			default:
				return null;
		}
	};

	const extractIndexedSubOptionAnswers = (type, id, form_question_options) => {
		const visibleOptions = form_question_options.filter((x) => !x.is_other);
		const indexedSubAnswers = [];

		const safeAnswers = answers || {};
		Object.keys(safeAnswers).forEach((key) => {
			const keyPattern = new RegExp(`^${id}_option_(\\d+)_sub_option_(\\d+)$`);
			const match = key.match(keyPattern);

			if (!match || !hasAnswerValue(safeAnswers[key])) {
				return;
			}

			const optionIndex = Number(match[1]);
			const subOptionIndex = Number(match[2]);
			const option = visibleOptions[optionIndex];
			const subOptions = (option?.form_question_options || []).filter(
				(x) => !x.is_other,
			);
			const subOption = subOptions[subOptionIndex];

			const formattedAnswer = checkTypeAnswer(type, safeAnswers[key]);

			if (!hasAnswerValue(formattedAnswer)) {
				return;
			}

			const labelParts = [option?.option, subOption?.option].filter(Boolean);

			if (labelParts.length) {
				indexedSubAnswers.push(`${labelParts.join(" - ")}: ${formattedAnswer}`);
				return;
			}

			indexedSubAnswers.push(formattedAnswer);
		});

		return indexedSubAnswers;
	};

	const checkTypeAnswer = (type, answer) => {
		switch (type) {
			case "Text (Input)":
			case "Number (Input)":
			case "Select (Dropdown)":
			case "Radio (Button)":
				return Array.isArray(answer) ? answer.join(", ") : answer;
			case "Date (Datepicker)":
				return answer ? dayjs(answer).format("MM/DD/YYYY") : null;
			case "Time (Timepicker)":
				return answer ? dayjs(answer).format("HH:mm:ss") : null;
			case "Checkbox":
				return Array.isArray(answer) ? answer.join(", ") : answer;
			default:
				return null;
		}
	};

	const hasAnswerValue = (value) => {
		if (value === undefined || value === null || value === "") {
			return false;
		}

		if (Array.isArray(value)) {
			return value.length > 0;
		}

		return true;
	};

	return (
		<Row>
			{question ? (
				<Col xs={24} sm={24} md={24} lg={24} xl={24}>
					<div
						className="quill-output question"
						dangerouslySetInnerHTML={{
							__html: question,
						}}
					/>
					{description && <div className="description">{description}</div>}
				</Col>
			) : null}

			<Col
				xs={24}
				sm={24}
				md={24}
				lg={24}
				xl={24}
				style={Array.isArray(id) ? { paddingLeft: 20 } : {}}
			>
				{renderQuestionOption()}
			</Col>
		</Row>
	);
}
